/**
 * Phase B+1 — IdeaWorkspace / AnalysisInsightView / AnalysisIdeaView 共通の MapLibre helper。
 *
 * 旧 `old/apps/user/src/components/ecw/IdeaForm.tsx` L523-598 (front/back mini-map) と
 * `InsightList.tsx` L102-141 (詳細 modal 地図) で繰り返し書かれていた DOM marker 構築 /
 * fitBounds idle 待ち / sentiment hsl 計算を 1 箇所に集約する。
 *
 * 設計方針:
 *   - 純粋関数 + DOM element factory に絞り、React state には触れない
 *   - 旧の `idea-map-marker` / `insight-map-marker` CSS クラスを継承 (色は inline で上書き)
 *   - maplibre-gl の型を直接 import すると Vitest の `vi.mock('maplibre-gl')` で
 *     LngLatBounds が存在しない問題が起きるため、利用側で `new maplibregl.LngLatBounds()`
 *     を作って渡す形にする (この module は map / bounds を入力として受ける)
 */

import type { Map as MaplibreMap, LngLatBounds as MaplibreLngLatBounds } from 'maplibre-gl';

/* ============================================================================
 * Palette & sentiment color helpers
 * ========================================================================== */

/** 旧 IdeaForm L554 + InsightList L57 踏襲: 8 色循環パレット (insight 別の色付け)。 */
export const INSIGHT_COLOR_PALETTE = [
  '#e74c3c',
  '#3498db',
  '#f1c40f',
  '#2ecc71',
  '#9b59b6',
  '#e67e22',
  '#1abc9c',
  '#34495e',
] as const;

/**
 * 0-based の index を渡すと PALETTE 色を循環 1 色返す。
 * 旧 IdeaForm L557 `colorPalette[insightIdx % colorPalette.length]` 等価。
 */
export function getInsightColor(index: number): string {
  if (!Number.isFinite(index)) return INSIGHT_COLOR_PALETTE[0];
  const i = Math.abs(Math.trunc(index));
  // `noUncheckedIndexedAccess` 環境では index 結果が string | undefined になるため明示 fallback。
  return INSIGHT_COLOR_PALETTE[i % INSIGHT_COLOR_PALETTE.length] ?? INSIGHT_COLOR_PALETTE[0];
}

/**
 * sentiment (-1..+1) を旧 InsightList L75-81 形式の `hsl(...)` に変換。
 *
 * `sentimentColor.ts` に既に `sentimentToHsl` がいるが、旧 InsightList の式
 * `hsl((1 - sentiment) / 2 * 240, 70%, 50%)` に「より直接的に」一致する別名を
 * insightMapHelpers から再 export しておく (旧仕様検証 / 再利用しやすさのため)。
 */
export function getSentimentHsl(sentiment: number): string {
  let v = sentiment;
  if (!Number.isFinite(v)) v = 0;
  if (v < -1) v = -1;
  if (v > 1) v = 1;
  const hue = ((1 - v) / 2) * 240;
  return `hsl(${hue}, 70%, 50%)`;
}

/* ============================================================================
 * DOM marker factory
 * ========================================================================== */

export interface NumberedMarkerOpts {
  /** active=true なら size 32 + z-index 999 + 太枠 (旧 idea-map-marker--active 風)。 */
  active?: boolean;
  /** 1 個目の (front mode の) marker は primary、それ以降は color override。default primary。 */
  color?: string;
  /** Marker クリック時 callback。 */
  onClick?: () => void;
  /** size の明示指定 (active 判定より優先)。 */
  size?: number;
}

/**
 * 番号付き丸 marker の HTMLElement を作成する。
 *
 * 旧 IdeaForm L545-547 (front mode) / L573-575 (back mode) を統合:
 *   - 円形 / 白枠 / シャドウ / 中央に番号
 *   - active 時は size を上げ z-index 999、border を太くする
 *
 * inline style で color/size を指定するのは MapLibre Marker {element} 仕様上必須
 * (CSS class だけだと動的色 / 動的サイズを切替えにくい)。preset 違反にはならない
 * (HSL の dynamic color は CSS custom property + style 経由 OK ルール内)。
 */
export function createNumberedMarkerEl(index: number, opts: NumberedMarkerOpts = {}): HTMLElement {
  const active = Boolean(opts.active);
  const size = opts.size ?? (active ? 32 : 24);
  const color = opts.color ?? '#00B4D8'; // primary-600 default
  const fontPx = Math.max(10, Math.round(size * 0.5));

  const outer = document.createElement('div');
  outer.setAttribute('data-marker-kind', 'insight-numbered');
  outer.setAttribute('data-active', active ? 'true' : 'false');
  outer.style.cssText = [
    `width:${size}px`,
    `height:${size}px`,
    'cursor:pointer',
    `z-index:${active ? 999 : 0}`,
  ].join(';');

  const dot = document.createElement('div');
  dot.style.cssText = [
    `width:${size}px`,
    `height:${size}px`,
    `background-color:${color}`,
    `border:${active ? 3 : 2}px solid #fff`,
    'border-radius:50%',
    'box-shadow:0 2px 5px rgba(0,0,0,0.3)',
    'display:flex',
    'align-items:center',
    'justify-content:center',
    'color:#fff',
    'font-weight:700',
    `font-size:${fontPx}px`,
    'line-height:1',
  ].join(';');
  dot.textContent = String(index + 1);
  outer.appendChild(dot);

  if (opts.onClick) {
    const handler = opts.onClick;
    outer.addEventListener('click', (e) => {
      e.stopPropagation();
      handler();
    });
  }

  return outer;
}

/* ============================================================================
 * fitBounds helper
 * ========================================================================== */

export interface FitBoundsOpts {
  padding?: number;
  maxZoom?: number;
  /** map.fitBounds の duration (ms)、default 0 = instant snap (旧 L588 踏襲)。 */
  duration?: number;
}

/**
 * `map.once('idle', () => map.fitBounds(...))` パターンの helper。
 *
 * 旧 IdeaForm L587-589 / InsightList L130-135 で繰り返されていた:
 *   - bounds に point が 1 つ以上含まれることが前提
 *   - `idle` を待ってから fitBounds (初回 layer 構築完了前の fitBounds はサイズ計算が
 *     不正なので必須)
 *   - duration:0 で snap、padding:20 / maxZoom:18 が旧の default
 *
 * `bounds` の中身 (point の有無) は呼出側で判定済 / 空なら呼ばないこと。
 */
export function fitBoundsOnce(
  map: MaplibreMap,
  bounds: MaplibreLngLatBounds,
  opts: FitBoundsOpts = {},
): void {
  const padding = opts.padding ?? 20;
  const maxZoom = opts.maxZoom ?? 18;
  const duration = opts.duration ?? 0;
  // idle 後に 1 回だけ実行 (idle が来ない jsdom mock 環境では何もしない)
  map.once('idle', () => {
    try {
      map.fitBounds(bounds, { padding, maxZoom, duration });
    } catch {
      // jsdom mock の fitBounds は未定義 → silent
    }
  });
}
